/**
 * useInlineNotice Hook
 * 
 * Manages InlineNotice state with:
 * - Single notice at a time (avoids stacking)
 * - Priority-based error handling
 * - Analytics event tracking for notice shown/dismissed
 * - Consistent recovery actions
 */
import { useState, useCallback, useRef, useEffect } from "react";
import apiClient from "../api/client";


// Error categories with priorities (higher = more important)
const ERROR_PRIORITIES = {
  network: 10,         // Network/connection errors
  auth: 9,             // Authentication errors  
  permission: 8,       // Permission denied
  validation: 7,       // Validation errors
  rate_limit: 6,       // Rate limiting
  server: 5,           // Server errors (500s)
  not_found: 4,        // Resource not found
  conflict: 3,         // Conflict errors
  generic: 1,          // Generic/unknown errors
  success: 0,          // Success messages
  info: 0,             // Info messages
  warning: 2,          // Warning messages
};

// Categorize error based on status code or message
function categorizeError(error) {
  const status = error?.response?.status;
  const message = error?.message?.toLowerCase() || "";
  const detail = error?.response?.data?.detail?.toLowerCase() || "";
  
  // Network errors (no response)
  if (!error?.response && (message.includes("network") || message.includes("failed to fetch"))) {
    return "network";
  }
  
  // Auth errors
  if (status === 401 || detail.includes("unauthorized") || detail.includes("invalid token")) {
    return "auth";
  }
  
  // Permission errors
  if (status === 403 || detail.includes("forbidden") || detail.includes("permission")) {
    return "permission";
  }
  
  // Rate limiting
  if (status === 429 || detail.includes("too many") || detail.includes("rate limit")) {
    return "rate_limit";
  }
  
  // Validation errors
  if (status === 400 || status === 422) {
    return "validation";
  }
  
  // Not found
  if (status === 404) {
    return "not_found";
  }
  
  // Conflict
  if (status === 409) {
    return "conflict";
  }
  
  // Server errors
  if (status >= 500) {
    return "server";
  }
  
  return "generic";
}

// Get user-friendly message based on category
function getRecoveryMessage(category, originalMessage) {
  switch (category) {
    case "network":
      return {
        title: "Connection issue",
        message: "Check your internet connection and try again.",
        actionLabel: "Retry",
      };
    case "auth":
      return {
        title: "Session expired",
        message: "Please sign in again to continue.",
        actionLabel: "Sign in",
        href: "/auth",
      };
    case "permission":
      return {
        title: "Access denied",
        message: "You don't have permission to do this.",
      };
    case "rate_limit":
      return {
        title: "Slow down",
        message: "Too many requests. Please wait a moment before trying again.",
      };
    case "validation":
      return {
        title: "Invalid input",
        message: originalMessage || "Please check your input and try again.",
      };
    case "server":
      return {
        title: "Something went wrong",
        message: "Our servers hit a snag. Please try again in a moment.",
        actionLabel: "Retry",
      };
    case "not_found":
      return {
        title: "Not found",
        message: originalMessage || "The requested resource could not be found.",
      };
    case "conflict":
      return {
        title: "Conflict",
        message: originalMessage || "This action conflicts with existing data.",
      };
    default:
      return {
        title: "Error",
        message: originalMessage || "Something went wrong. Please try again.",
        actionLabel: "Retry",
      };
  }
}

/**
 * Track analytics event for notice
 */
async function trackNoticeEvent(eventName, properties, token) {
  try {
    await apiClient.post(
      `/analytics/track`,
      {
        event_name: eventName,
        props: properties,
      },
      {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }
    );
  } catch (err) {
    // Silent fail - don't let analytics break the app
    console.debug("[useInlineNotice] Analytics tracking failed:", err);
  }
}

/**
 * useInlineNotice Hook
 * 
 * @param {string} surface - The surface/page name for analytics (e.g., "verification", "connections")
 * @param {string} token - Auth token for API calls
 * @returns {Object} Notice state and handlers
 */
export function useInlineNotice(surface, token) {
  const [notice, setNotice] = useState(null);
  const currentPriorityRef = useRef(0);
  const dismissTimeoutRef = useRef(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dismissTimeoutRef.current) {
        clearTimeout(dismissTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Dismiss the current notice
   */
  const dismissNotice = useCallback(() => {
    setNotice((currentNotice) => {
      if (currentNotice) {
        // Track notice dismissed
        const duration = Date.now() - (currentNotice.shownAt || Date.now());
        trackNoticeEvent("inline_notice_dismissed", {
          surface,
          variant: currentNotice.variant,
          category: currentNotice.category || "unknown",
          duration_ms: duration,
        }, token);
      }
      return null;
    });

    // Clear timeout
    if (dismissTimeoutRef.current) {
      clearTimeout(dismissTimeoutRef.current);
      dismissTimeoutRef.current = null;
    }

    currentPriorityRef.current = 0;
  }, [surface, token]);

  /**
   * Show a notice, replacing current if higher priority
   */
  const showNotice = useCallback(
    ({ variant = "error", title, message, actionLabel, onAction, href, category, autoDismiss = false }) => {
      const priority = ERROR_PRIORITIES[category] || ERROR_PRIORITIES.generic;

      // Only replace if higher or equal priority
      if (priority >= currentPriorityRef.current) {
        // Clear any existing auto-dismiss timeout
        if (dismissTimeoutRef.current) {
          clearTimeout(dismissTimeoutRef.current);
          dismissTimeoutRef.current = null;
        }

        currentPriorityRef.current = priority;

        const noticeData = {
          variant,
          title,
          message,
          actionLabel,
          onAction,
          href,
          category,
          shownAt: Date.now(),
        };

        setNotice(noticeData);

        // Track notice shown
        trackNoticeEvent("inline_notice_shown", {
          surface,
          variant,
          category: category || "unknown",
          title,
        }, token);

        // Auto-dismiss for success/info notices after 5 seconds
        if (autoDismiss || variant === "success") {
          dismissTimeoutRef.current = setTimeout(() => {
            setNotice(null);
            currentPriorityRef.current = 0;
          }, 5000);
        }
      }
    },
    [surface, token]
  );

  /**
   * Show error notice from an error object
   */
  const showError = useCallback(
    (error, { customMessage, onRetry } = {}) => {
      const category = categorizeError(error);
      const originalMessage =
        customMessage ||
        error?.response?.data?.detail ||
        error?.message ||
        "An error occurred";
      
      const recovery = getRecoveryMessage(category, originalMessage);

      showNotice({
        variant: "error",
        title: recovery.title,
        message: recovery.message,
        actionLabel: onRetry ? (recovery.actionLabel || "Retry") : recovery.actionLabel,
        onAction: onRetry,
        href: recovery.href,
        category,
      });
    },
    [showNotice]
  );

  /**
   * Show success notice
   */
  const showSuccess = useCallback(
    (message, { title = "Success", autoDismiss = true } = {}) => {
      // Reset priority for success (allow errors to override)
      currentPriorityRef.current = 0;
      
      showNotice({
        variant: "success",
        title,
        message,
        category: "success",
        autoDismiss,
      });
    },
    [showNotice]
  );

  /**
   * Show warning notice
   */
  const showWarning = useCallback(
    (message, { title = "Warning", actionLabel, onAction } = {}) => {
      showNotice({
        variant: "warning",
        title,
        message,
        actionLabel,
        onAction,
        category: "warning",
      });
    },
    [showNotice]
  );

  /**
   * Show info notice
   */
  const showInfo = useCallback(
    (message, { title = "Info", actionLabel, onAction, autoDismiss = true } = {}) => {
      // Reset priority for info (allow errors to override)
      currentPriorityRef.current = 0;
      
      showNotice({
        variant: "info",
        title,
        message,
        actionLabel,
        onAction,
        category: "info",
        autoDismiss,
      });
    },
    [showNotice]
  );

  /**
   * Clear notice (alias for dismissNotice)
   */
  const clearNotice = dismissNotice;

  return {
    notice,
    showNotice,
    showError,
    showSuccess,
    showWarning,
    showInfo,
    dismissNotice,
    clearNotice,
  };
}

export default useInlineNotice;
